import React, { useState, useRef } from 'react';
import { PhoneIcon, MailIcon, CalendarIcon, StickyNoteIcon, ChevronDownIcon, ChevronUpIcon, CheckCircle2Icon, ClockIcon, ExternalLinkIcon, PlayCircleIcon } from 'lucide-react';
import { Activity } from '../data/mockData';
interface TimelineItemProps {
  activity: Activity;
  isLast?: boolean;
}
export function TimelineItem({
  activity,
  isLast = false
}: TimelineItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  // Format timestamp
  const dateObj = new Date(activity.timestamp);
  const dateStr = dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  const timeStr = dateObj.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit'
  });
  // Format duration
  const formatDuration = (seconds?: number) => {
    if (!seconds) return null;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  };
  // Determine styling based on activity type and status
  const getTypeConfig = () => {
    switch (activity.type) {
      case 'call':
        const isSuccess = activity.disposition === 'Connected';
        const isWarning = activity.disposition === 'Voicemail' || activity.disposition === 'Left Message';
        return {
          icon: <PhoneIcon className="w-5 h-5" data-id="element-180" />,
          iconBg: isSuccess ? 'bg-emerald-100 text-emerald-600 ring-emerald-50' : isWarning ? 'bg-amber-100 text-amber-600 ring-amber-50' : 'bg-red-100 text-red-600 ring-red-50',
          cardBorder: isSuccess ? 'border-l-4 border-l-emerald-500' : isWarning ? 'border-l-4 border-l-amber-500' : 'border-l-4 border-l-red-500',
          highlight: activity.isMeaningful
        };
      case 'email':
        const isOpened = activity.emailStatus === 'opened';
        return {
          icon: <MailIcon className="w-5 h-5" data-id="element-181" />,
          iconBg: 'bg-blue-100 text-blue-600 ring-blue-50',
          cardBorder: isOpened ? 'border-l-4 border-l-blue-400' : 'border-l-4 border-l-gray-300',
          highlight: false
        };
      case 'event':
        return {
          icon: <CalendarIcon className="w-5 h-5" data-id="element-182" />,
          iconBg: 'bg-indigo-100 text-indigo-600 ring-indigo-50',
          cardBorder: 'border-l-4 border-l-indigo-400',
          highlight: false
        };
      case 'note':
        return {
          icon: <StickyNoteIcon className="w-5 h-5" data-id="element-183" />,
          iconBg: 'bg-gray-100 text-gray-600 ring-gray-50',
          cardBorder: 'border-l-4 border-l-gray-300',
          highlight: false
        };
    }
  };
  const config = getTypeConfig();
  return <div className="relative flex gap-6 group" data-id="element-184">
      {/* Timeline Line */}
      {!isLast && <div className="absolute left-6 top-12 bottom-0 w-px bg-gray-200 -ml-px group-hover:bg-gray-300 transition-colors" data-id="element-185" />}

      {/* Timeline Icon */}
      <div className="relative z-10 flex-shrink-0" data-id="element-186">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ring-4 ${config.iconBg}`} data-id="element-187">
          {config.icon}
        </div>
      </div>

      {/* Content Card */}
      <div className="flex-1 pb-8" data-id="element-188">
        <div className={`bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden ${config.cardBorder}`} onClick={() => setIsExpanded(!isExpanded)} role="button" tabIndex={0} aria-expanded={isExpanded} onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setIsExpanded(!isExpanded);
        }
      }} data-id="element-189">
          {/* Collapsed Header */}
          <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-190">
            <div className="flex-1" data-id="element-191">
              <div className="flex items-center gap-2 mb-1" data-id="element-192">
                <span className="text-sm font-medium text-gray-500" data-id="element-193">
                  {dateStr} at {timeStr}
                </span>
                {activity.type === 'note' && activity.author && <span className="text-sm text-gray-400" data-id="element-194">
                    • by {activity.author}
                  </span>}
              </div>
              <h3 className={`text-base font-semibold ${config.highlight ? 'text-gray-900' : 'text-gray-800'}`} data-id="element-195">
                {activity.summary}
              </h3>

              {/* Type-specific quick metadata */}
              {activity.type === 'note' && !isExpanded && activity.details && <p className="text-sm text-gray-500 mt-1 line-clamp-1 italic" data-id="element-196">
                  {activity.details}
                </p>}
            </div>

            {/* Right side metadata & badges */}
            <div className="flex items-center gap-3 sm:justify-end" data-id="element-197">
              {/* Call specific badges */}
              {activity.type === 'call' && <>
                  {activity.duration && <div className="flex items-center text-sm font-medium text-gray-700 bg-gray-100 px-2.5 py-1 rounded-md" data-id="element-198">
                      <ClockIcon className="w-3.5 h-3.5 mr-1.5 text-gray-500" data-id="element-199" />
                      {formatDuration(activity.duration)}
                    </div>}
                  {activity.disposition && <span className={`text-xs font-bold px-2.5 py-1 rounded-full border
                      ${activity.disposition === 'Connected' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : activity.disposition === 'Voicemail' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-700 border-red-200'}`} data-id="element-200">
                      {activity.disposition}
                    </span>}
                  {activity.isMeaningful && <div className="flex items-center text-emerald-600" title="Meaningful Interaction" data-id="element-201">
                      <CheckCircle2Icon className="w-5 h-5" data-id="element-202" />
                    </div>}
                </>}

              {/* Email specific badges */}
              {activity.type === 'email' && activity.emailStatus && <span className={`text-xs font-bold px-2.5 py-1 rounded-full border
                  ${activity.emailStatus === 'opened' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`} data-id="element-203">
                  {activity.emailStatus === 'opened' ? 'Opened' : 'Sent'}
                </span>}

              {/* Event specific badges */}
              {activity.type === 'event' && activity.eventType && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200" data-id="element-204">
                  {activity.eventType}
                </span>}

              <div className="text-gray-400 w-8 flex justify-center" data-id="element-205">
                {isExpanded ? <ChevronUpIcon className="w-5 h-5" data-id="element-206" /> : <ChevronDownIcon className="w-5 h-5" data-id="element-207" />}
              </div>
            </div>
          </div>

          {/* Expanded Content with smooth height transition */}
          <div className="grid transition-all duration-300 ease-in-out" style={{
          gridTemplateRows: isExpanded ? '1fr' : '0fr'
        }} data-id="element-208">
            <div className="overflow-hidden" data-id="element-209">
              <div className="px-4 pb-4 pt-2 border-t border-gray-100 bg-gray-50/50" data-id="element-210">
                <div className="prose prose-sm max-w-none text-gray-600 mt-2" data-id="element-211">
                  <p className="whitespace-pre-wrap" data-id="element-212">{activity.details}</p>
                </div>

                {/* Action Links */}
                <div className="mt-4 flex gap-2" data-id="element-213">
                  <button className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-3 py-1.5 rounded-md flex items-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" onClick={e => e.stopPropagation()} data-id="element-214">
                    <ExternalLinkIcon className="w-4 h-4 mr-1.5" data-id="element-215" />
                    View Full Record
                  </button>
                  {activity.type === 'call' && activity.disposition === 'Connected' && <button className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-3 py-1.5 rounded-md flex items-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" onClick={e => e.stopPropagation()} data-id="element-216">
                        <PlayCircleIcon className="w-4 h-4 mr-1.5" data-id="element-217" />
                        Play Recording
                      </button>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}